import { useState, useMemo } from 'react'
import { ConfirmDialog, PageHeader } from '@lipaflow/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, usePermission, type SchoolGrade, type SchoolStream } from '@lipaflow/shared'
import {
  PlusIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import { fetchGrades, fetchStreams } from '../services/schoolStructure'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EventType = 'class' | 'exam' | 'holiday' | 'activity' | 'meeting' | 'other'

interface TimetableEvent {
  id: string
  title: string
  type: EventType
  date: string
  start_time: string
  end_time: string
  grade_id: string | null
  stream_id: string | null
  subject: string | null
  location: string | null
  created_at: string
}

/** What the API sends and accepts. The page had its own field names, which the API ignored or rejected. */
interface ApiEvent {
  id: string
  title: string
  event_type: EventType
  starts_at: string
  ends_at: string | null
  grade_id: string | null
  stream_id: string | null
  subject: string | null
  location: string | null
  created_at: string
}

// Wall-clock parts straight from the ISO text, so no timezone shifts a lesson.
const fromApi = (e: ApiEvent): TimetableEvent => ({
  id: e.id, title: e.title, type: e.event_type, date: e.starts_at.slice(0, 10),
  start_time: e.starts_at.slice(11, 16), end_time: (e.ends_at ?? e.starts_at).slice(11, 16),
  grade_id: e.grade_id, stream_id: e.stream_id, subject: e.subject, location: e.location, created_at: e.created_at,
})
const toApi = (f: EventFormState) => ({
  title: f.title.trim(), event_type: f.type,
  starts_at: `${f.date}T${f.start_time}:00`, ends_at: `${f.date}T${f.end_time}:00`,
  grade_id: f.grade_id || null, stream_id: f.grade_id && f.stream_id ? f.stream_id : null,
  subject: f.subject || null, location: f.location || null,
})

interface EventFormState {
  title: string
  type: EventType
  date: string
  start_time: string
  end_time: string
  grade_id: string
  stream_id: string
  subject: string
  location: string
}

const EMPTY_FORM: EventFormState = {
  title: '',
  type: 'class',
  date: '',
  start_time: '08:00',
  end_time: '09:00',
  grade_id: '',
  stream_id: '',
  subject: '',
  location: '',
}

const EVENT_TYPES: { value: EventType; label: string }[] = [
  { value: 'class', label: 'Class' },
  { value: 'exam', label: 'Exam' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'activity', label: 'Activity' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'other', label: 'Other' },
]

// Tokens only (brand doc §8): a holiday isn't an error, so never danger.
const EVENT_COLORS: Record<EventType, { bg: string; text: string; border: string }> = {
  class: { bg: 'bg-accent-soft', text: 'text-ink', border: 'border-accent' },
  exam: { bg: 'bg-warning-soft', text: 'text-ink', border: 'border-warning' },
  holiday: { bg: 'bg-success-soft', text: 'text-ink', border: 'border-success' },
  activity: { bg: 'bg-surface-alt', text: 'text-ink', border: 'border-accent' },
  meeting: { bg: 'bg-surface-alt', text: 'text-ink', border: 'border-hairline' },
  other: { bg: 'bg-surface-alt', text: 'text-ink', border: 'border-hairline' },
}

const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const

// Generate time slots from 7am to 5pm
const TIME_SLOTS: string[] = []
for (let h = 7; h <= 17; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, '0')}:00`)
}

function getMonday(d: Date): Date {
  const date = new Date(d)
  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1)
  date.setDate(diff)
  date.setHours(0, 0, 0, 0)
  return date
}

function addDays(d: Date, days: number): Date {
  const date = new Date(d)
  date.setDate(date.getDate() + days)
  return date
}

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function formatWeekRange(monday: Date): string {
  const friday = addDays(monday, 4)
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  return `${monday.toLocaleDateString(undefined, options)} - ${friday.toLocaleDateString(undefined, options)}, ${monday.getFullYear()}`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function TimetablePage() {
  const queryClient = useQueryClient()
  const canView = usePermission('timetable:view')
  const canManage = usePermission('timetable:manage')

  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()))
  const [gradeFilter, setGradeFilter] = useState('')
  const [streamFilter, setStreamFilter] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState<EventFormState>(EMPTY_FORM)
  // Events could be added but never changed or removed, though the API allows both.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const { data: grades = [] } = useQuery<SchoolGrade[]>({
    queryKey: ['school-structure', 'grades'],
    queryFn: fetchGrades,
    enabled: canView,
  })

  const { data: streams = [] } = useQuery<SchoolStream[]>({
    queryKey: ['school-structure', 'streams'],
    queryFn: fetchStreams,
    enabled: canView,
  })

  const gradeMap = useMemo(
    () => Object.fromEntries(grades.map((g) => [g.id, g.display_name])),
    [grades],
  )

  const streamMap = useMemo(
    () => Object.fromEntries(streams.map((s) => [s.id, s.display_name])),
    [streams],
  )

  const weekEnd = useMemo(() => addDays(weekStart, 4), [weekStart])

  // Weekday dates
  const weekDates = useMemo(
    () => Array.from({ length: 5 }, (_, i) => toIso(addDays(weekStart, i))),
    [weekStart],
  )

  // Fetch events for the visible week
  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['timetable', 'events', toIso(weekStart), toIso(weekEnd), gradeFilter, streamFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('date_from', toIso(weekStart))
      params.set('date_to', toIso(weekEnd))
      if (gradeFilter) params.set('grade_id', gradeFilter)
      // The API needs a class whenever a stream is given.
      if (gradeFilter && streamFilter) params.set('stream_id', streamFilter)
      const res = await api.get<{ items: ApiEvent[] } | ApiEvent[]>(`/education/timetable/events?${params.toString()}`)
      const items = Array.isArray(res.data) ? res.data : res.data.items
      return { items: items.map(fromApi), total: items.length }
    },
    enabled: canView,
  })

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map: Record<string, TimetableEvent[]> = {}
    for (const date of weekDates) {
      map[date] = []
    }
    for (const event of eventsData?.items ?? []) {
      const dateKey = event.date.slice(0, 10)
      if (map[dateKey]) {
        map[dateKey].push(event)
      }
    }
    return map
  }, [eventsData, weekDates])

  // Create event mutation
  const createMutation = useMutation({
    mutationFn: async (payload: EventFormState) => {
      const body = toApi(payload)
      const res = editingId
        ? await api.patch(`/education/timetable/events/${editingId}`, body)
        : await api.post('/education/timetable/events', body)
      return res.data
    },
    onSuccess: () => {
      toast.success(editingId ? 'Saved' : 'Added to the timetable')
      queryClient.invalidateQueries({ queryKey: ['timetable'] })
      closeModal()
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string } } }
      toast.error(axiosErr.response?.data?.detail || "Couldn't save it")
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => api.delete(`/education/timetable/events/${editingId}`),
    onSuccess: () => {
      toast.success('Removed from the timetable')
      queryClient.invalidateQueries({ queryKey: ['timetable'] })
      setDeleting(false)
      closeModal()
    },
    onError: () => toast.error("Couldn't remove it"),
  })

  const closeModal = () => {
    setModalOpen(false)
    setForm(EMPTY_FORM)
    setEditingId(null)
  }

  const openEdit = (event: TimetableEvent) => {
    setForm({
      title: event.title, type: event.type, date: event.date.slice(0, 10),
      start_time: event.start_time.slice(0, 5), end_time: event.end_time.slice(0, 5),
      grade_id: event.grade_id ?? '', stream_id: event.stream_id ?? '',
      subject: event.subject ?? '', location: event.location ?? '',
    })
    setEditingId(event.id)
    setModalOpen(true)
  }

  const openCreate = (date?: string) => {
    setForm({
      ...EMPTY_FORM,
      date: date || toIso(weekStart),
    })
    setModalOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim() || !form.date || !form.start_time || !form.end_time) {
      toast.error('Please fill in all required fields')
      return
    }
    createMutation.mutate(form)
  }

  const goToPreviousWeek = () => setWeekStart((prev) => addDays(prev, -7))
  const goToNextWeek = () => setWeekStart((prev) => addDays(prev, 7))
  const goToToday = () => setWeekStart(getMonday(new Date()))


  if (!canView) {
    return (
      <div className="bg-surface rounded-card border border-hairline p-8 text-center">
        <h2 className="text-lg font-semibold text-ink">The timetable is for teachers and school leaders</h2>
        <p className="text-ink-muted mt-2">Ask your admin if you need to see it.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader className="flex-1 min-w-0" title="Timetable" subtitle="The week for each class: lessons, exams and events." />
        {canManage && (
          <button
            onClick={() => openCreate()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-on-accent rounded-button text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add to timetable
          </button>
        )}
      </div>

      {/* Navigation & Filters */}
      <div className="bg-surface rounded-card border border-hairline p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousWeek}
              className="p-1.5 text-ink-muted hover:text-ink hover:bg-surface-alt rounded-button transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm font-medium text-accent bg-accent-soft rounded-button hover:bg-accent-soft transition-colors"
            >
              Today
            </button>
            <button
              onClick={goToNextWeek}
              className="p-1.5 text-ink-muted hover:text-ink hover:bg-surface-alt rounded-button transition-colors"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
            <span className="text-sm font-semibold text-ink ml-2">{formatWeekRange(weekStart)}</span>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={gradeFilter}
              onChange={(e) => setGradeFilter(e.target.value)}
              className="px-3 py-1.5 border border-hairline rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              <option value="">All grades</option>
              {grades.map((g) => (
                <option key={g.id} value={g.id}>{g.display_name}</option>
              ))}
            </select>
            <select
              value={streamFilter}
              onChange={(e) => setStreamFilter(e.target.value)}
              className="px-3 py-1.5 border border-hairline rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
            >
              <option value="">All streams</option>
              {streams.map((s) => (
                <option key={s.id} value={s.id}>{s.display_name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-hairline">
          {EVENT_TYPES.map((t) => {
            const colors = EVENT_COLORS[t.value]
            return (
              <div key={t.value} className="flex items-center gap-1.5">
                <div className={clsx('w-3 h-3 rounded', colors.bg, 'border', colors.border)} />
                <span className="text-xs text-ink-muted">{t.label}</span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Weekly Grid */}
      {isLoading ? (
        <div className="bg-surface rounded-card border border-hairline p-12 text-center">
          <div className="w-6 h-6 spinner mx-auto" />
        </div>
      ) : (
        <div className="bg-surface rounded-card border border-hairline overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Day Headers */}
              <div className="grid grid-cols-[72px_repeat(5,1fr)] border-b border-hairline">
                <div className="p-2 bg-surface-alt" />
                {weekDates.map((date, idx) => {
                  const isToday = date === toIso(new Date())
                  return (
                    <div
                      key={date}
                      className={clsx(
                        'p-3 text-center border-l border-hairline',
                        isToday ? 'bg-accent-soft' : 'bg-surface-alt',
                      )}
                    >
                      <div className={clsx('text-xs font-medium', isToday ? 'text-accent' : 'text-ink-muted')}>
                        {WEEKDAY_SHORT[idx]}
                      </div>
                      <div className={clsx('text-sm font-semibold mt-0.5', isToday ? 'text-accent' : 'text-ink')}>
                        {new Date(date + 'T00:00:00').getDate()}
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Time Grid */}
              {TIME_SLOTS.map((time) => {
                const hour = parseInt(time.split(':')[0])
                return (
                  <div key={time} className="grid grid-cols-[72px_repeat(5,1fr)] border-b border-hairline min-h-[56px]">
                    <div className="p-2 text-right pr-3">
                      <span className="text-xs text-ink-faint">
                        {hour <= 12 ? `${hour}:00` : `${hour - 12}:00`} {hour < 12 ? 'AM' : 'PM'}
                      </span>
                    </div>
                    {weekDates.map((date) => {
                      const dayEvents = (eventsByDate[date] ?? []).filter((ev) => {
                        const evHour = parseInt(ev.start_time.split(':')[0])
                        return evHour === hour
                      })
                      const isToday = date === toIso(new Date())
                      return (
                        <div
                          key={date}
                          className={clsx(
                            'border-l border-hairline p-1 relative',
                            isToday && 'bg-accent-soft/30',
                            canManage && 'cursor-pointer hover:bg-surface-alt',
                          )}
                          onClick={() => canManage && openCreate(date)}
                        >
                          {dayEvents.map((event) => {
                            const colors = EVENT_COLORS[event.type] || EVENT_COLORS.other
                            return (
                              <div
                                key={event.id}
                                role={canManage ? 'button' : undefined}
                                tabIndex={canManage ? 0 : undefined}
                                onClick={(e) => { if (canManage) { e.stopPropagation(); openEdit(event) } }}
                                onKeyDown={(e) => { if (canManage && e.key === 'Enter') { e.stopPropagation(); openEdit(event) } }}
                                className={clsx(
                                  'rounded px-1.5 py-1 mb-1 border-l-2 text-xs',
                                  colors.bg,
                                  colors.text,
                                  colors.border,
                                )}
                                title={`${event.title} (${event.start_time} - ${event.end_time})`}
                              >
                                <div className="font-medium truncate">{event.title}</div>
                                <div className="text-[10px] opacity-75">
                                  {event.start_time} - {event.end_time}
                                  {event.location ? ` | ${event.location}` : ''}
                                </div>
                                {event.grade_id && (
                                  <div className="text-[10px] opacity-75 truncate">
                                    {gradeMap[event.grade_id] || event.grade_id}
                                    {event.stream_id ? ` / ${streamMap[event.stream_id] || event.stream_id}` : ''}
                                    {event.subject ? ` - ${event.subject}` : ''}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Empty state if no events at all */}
      {!isLoading && (eventsData?.items?.length ?? 0) === 0 && (
        <div className="bg-surface rounded-card border border-hairline p-12 text-center">
          <CalendarDaysIcon className="h-10 w-10 text-ink-faint mx-auto mb-3" />
          <p className="text-sm font-medium text-ink-muted">No events this week</p>
          <p className="text-xs text-ink-faint mt-1">
            {canManage
              ? 'Click a time slot to add a lesson, exam or event.'
              : 'No timetable events have been scheduled for this week.'}
          </p>
        </div>
      )}

      {/* Create Event Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-ink/40 backdrop-blur-sm"
            onClick={closeModal}
          />
          <div className="relative bg-surface rounded-card shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
              <h2 className="font-outfit text-lg font-semibold text-ink">{editingId ? 'Edit' : 'Add to the timetable'}</h2>
              <button
                onClick={closeModal}
                className="p-1.5 text-ink-faint hover:text-ink-muted rounded-button hover:bg-surface-alt transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Event title"
                  className="w-full px-3 py-2 border border-hairline rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Type *</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as EventType })}
                    className="w-full px-3 py-2 border border-hairline rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    {EVENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Date *</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full px-3 py-2 border border-hairline rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Start Time *</label>
                  <input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    className="w-full px-3 py-2 border border-hairline rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">End Time *</label>
                  <input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    className="w-full px-3 py-2 border border-hairline rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Grade</label>
                  <select
                    value={form.grade_id}
                    onChange={(e) => setForm({ ...form, grade_id: e.target.value })}
                    className="w-full px-3 py-2 border border-hairline rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    <option value="">All grades / none</option>
                    {grades.map((g) => (
                      <option key={g.id} value={g.id}>{g.display_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Stream</label>
                  <select
                    value={form.stream_id}
                    onChange={(e) => setForm({ ...form, stream_id: e.target.value })}
                    className="w-full px-3 py-2 border border-hairline rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    <option value="">All streams</option>
                    {streams.map((s) => (
                      <option key={s.id} value={s.id}>{s.display_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Subject</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="e.g. Mathematics"
                    className="w-full px-3 py-2 border border-hairline rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Location</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="e.g. Room 201"
                    className="w-full px-3 py-2 border border-hairline rounded-button text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                {editingId && (
                  <button type="button" onClick={() => setDeleting(true)} className="mr-auto px-3 py-2 text-sm text-ink-muted hover:underline">
                    Remove
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-hairline rounded-button text-sm text-ink-muted hover:bg-surface-alt transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 bg-accent text-on-accent rounded-button text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors"
                >
                  {createMutation.isPending ? 'Saving…' : editingId ? 'Save changes' : 'Add to timetable'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmDialog open={deleting} onClose={() => setDeleting(false)} onConfirm={() => deleteMutation.mutate()}
        title={`Remove “${form.title}” from the timetable?`} confirmLabel="Remove" variant="danger" isPending={deleteMutation.isPending}>
        It disappears from this week's timetable for everyone.
      </ConfirmDialog>
    </div>
  )
}
